/**
 * Home health demo — default configuration fixture.
 *
 * Fictional branch "Seattle" (Pacific time, so the presenter's browser, the
 * device and the data agree). Everything here is the "agency
 * configures…" surface the script points at: trait vocabulary, visit-type
 * catalog (planned durations calibrated to observed actuals — an SOC with OASIS
 * runs about 2.5 h in the home — and point values, the planning unit, from
 * their table; every point value is a placeholder until we have that table),
 * weekly points targets and ceilings per employment type, the documentation
 * block, the SOC slot (planned SOC duration + assumed drive), readiness sets per
 * payer (internal), LUPA threshold, starter defaults (archetypes), service
 * area / closed zips / territories, inflow assumption.
 */
import type { ConfigState, Discipline, Employment, VisitTypeConfig, VisitTypeKind } from "../state";

export const TRAIT_OASIS = "OASIS-competent";
export const TRAIT_WOUND_VAC = "Wound VAC";
export const TRAIT_IV = "IV therapy";
export const TRAIT_SPANISH = "Spanish-speaking";

/**
 * **The same trait, in the tenant's own vocabulary.** A trait is a display
 * string in here and a `(trait, value)` pair in `marlin.provider_trait`, where
 * the column is the category and the value is the specific one — `gender/male`,
 * `language/english`. The product's own matcher (`traitsMatch` in
 * `services/scheduleOptions.ts`) is an exact match over both columns and knows
 * no vocabulary, so what matters is only that the seed and the demo spell a
 * trait the same way. This map is the one place that decides.
 *
 * `Spanish-speaking` is deliberately **not** a certification: the tenant already
 * carries `language/spanish` from the roster's own `languages`, and giving the
 * same fact two spellings would let the real engine and this one disagree about
 * who speaks Spanish.
 */
export const TENANT_TRAITS: Record<string, { trait: string; value: string }> = {
  [TRAIT_OASIS]: { trait: "certification", value: "oasis" },
  [TRAIT_WOUND_VAC]: { trait: "certification", value: "woundVac" },
  [TRAIT_IV]: { trait: "certification", value: "ivTherapy" },
  [TRAIT_SPANISH]: { trait: "language", value: "spanish" },
};

const ASSESSING: Discipline[] = ["RN", "PT", "OT", "SLP"];

function vt(
  kind: VisitTypeKind,
  label: string,
  disciplines: Discipline[],
  plannedMinutes: number,
  points: number,
  opts: { requiredTraits?: string[]; isAssessment?: boolean } = {},
): VisitTypeConfig {
  return {
    kind,
    label,
    disciplines,
    plannedMinutes,
    points,
    requiredTraits: opts.requiredTraits ?? [],
    isAssessment: opts.isAssessment ?? false,
  };
}

export const DEFAULT_VISIT_TYPES: Record<VisitTypeKind, VisitTypeConfig> = {
  SOC: vt("SOC", "Start of care", ASSESSING, 150, 2.5, { requiredTraits: [TRAIT_OASIS], isAssessment: true }),
  ROC: vt("ROC", "Resumption of care", ASSESSING, 105, 1.75, { requiredTraits: [TRAIT_OASIS], isAssessment: true }),
  RECERT: vt("RECERT", "Recertification", ASSESSING, 105, 1.75, { requiredTraits: [TRAIT_OASIS], isAssessment: true }),
  DISCHARGE_ASSESSMENT: vt("DISCHARGE_ASSESSMENT", "Discharge assessment", ASSESSING, 75, 1.5, {
    requiredTraits: [TRAIT_OASIS],
    isAssessment: true,
  }),
  SN_ROUTINE: vt("SN_ROUTINE", "Skilled nursing visit", ["RN", "LPN"], 45, 1.0),
  RN_SUPERVISORY: vt("RN_SUPERVISORY", "RN supervisory visit", ["RN"], 30, 0.5),
  PT_EVAL: vt("PT_EVAL", "PT evaluation", ["PT"], 60, 1.5),
  PT_ROUTINE: vt("PT_ROUTINE", "PT visit", ["PT", "PTA"], 45, 1.0),
  OT_EVAL: vt("OT_EVAL", "OT evaluation", ["OT"], 60, 1.5),
  OT_ROUTINE: vt("OT_ROUTINE", "OT visit", ["OT", "COTA"], 45, 1.0),
  SLP_EVAL: vt("SLP_EVAL", "SLP evaluation", ["SLP"], 60, 1.5),
  SLP_ROUTINE: vt("SLP_ROUTINE", "SLP visit", ["SLP"], 45, 1.0),
  MSW_VISIT: vt("MSW_VISIT", "Social work visit", ["MSW"], 60, 1.5),
  HHA_VISIT: vt("HHA_VISIT", "Home health aide visit", ["HHA"], 60, 1.0),
  // Colin, 2026-09-18, naming the weights that matter: *"therapy 30-day
  // reassessments: 1.25 units"*. The fourth of his four, and the only one the
  // catalogue was missing — 2.5, 1.5 and 1.75 were already here. It is on the
  // branch's visit-type table so the points model on screen is his, whole.
  THERAPY_REASSESSMENT: vt("THERAPY_REASSESSMENT", "Therapy 30-day reassessment", ["PT", "OT", "SLP"], 60, 1.25, { isAssessment: true }),
};

export const TERRITORIES = [
  { name: "North", zips: ["98103", "98105", "98107", "98115", "98117", "98125", "98133"] },
  { name: "East", zips: ["98102", "98108", "98112", "98118", "98122", "98144"] },
  { name: "South", zips: ["98106", "98116", "98126", "98136", "98146"] },
  { name: "West", zips: ["98101", "98104", "98109", "98119", "98121", "98199"] },
];

export const CLOSED_ZIPS = ["98177", "98004"];

/** Weekly points target and ceiling by employment type. Per diem has no target; the ceiling is the most a declared day pattern would carry. */
export const TARGET_POINTS_BY_EMPLOYMENT: Record<Employment, number> = { FT: 30, PT: 12, perDiem: 0 };
export const CEILING_POINTS_BY_EMPLOYMENT: Record<Employment, number> = { FT: 33, PT: 14, perDiem: 24 };

/**
 * The morning documentation catch-up every full-time clinician carries, one hour on the calendar (Pete,
 * 2026-10-07). The day's first visit is still at 10: the seed keeps the morning clear to `FIRST_VISIT_AT`
 * when it places visits, so shortening the block moved no visit.
 */
export const DOCUMENTATION_BLOCK = { start: "08:00", end: "09:00" };
export const FIRST_VISIT_AT = "10:00";

export const DEFAULT_CONFIG: ConfigState = {
  branch: {
    name: "Seattle",
    timeZone: "America/Los_Angeles",
    serviceAreaZips: [...TERRITORIES.flatMap((t) => t.zips), "98134", "98177", "98004"],
    closedZips: CLOSED_ZIPS,
    territories: TERRITORIES,
  },
  traitVocabulary: [TRAIT_OASIS, TRAIT_WOUND_VAC, TRAIT_IV, TRAIT_SPANISH],
  visitTypes: DEFAULT_VISIT_TYPES,
  socSlot: { durationMinutes: 150, driveMinutes: 30 },
  gateSets: {
    MA: ["orders", "readinessCall", "consent", "auth"],
    FFS: ["orders", "readinessCall", "consent"],
  },
  certPeriodDays: 60,
  paymentPeriodDays: 30,
  lupaThresholdPerPeriod: 4,
  /**
   * Expected-plan templates: the branch's starter profiles, keyed by case type.
   * `starterFrequencies` is the pattern per discipline and `planTemplateNames` is
   * what the dropdown and the queue read; the key itself never renders.
   *
   * Every pattern and every name here is a PLACEHOLDER for the demo. In
   * production these are the branch's own, configured by the agency.
   */
  planTemplateNames: {
    woundCareSnPt: "Wound care, nursing-led",
    woundCareSnDecay: "Wound care, tapering",
    chfSn: "Heart failure, nursing only",
    orthoPtOt: "Orthopedic recovery, therapy-led",
    strokeSnPtOtSlp: "Stroke recovery, full team",
    hhaSupport: "Aide support with nursing oversight",
    copdSnHha: "Lung disease, nursing with aide",
    therapyPtSlp: "Therapy only, movement and speech",
  },
  starterFrequencies: {
    woundCareSnPt: [
      { discipline: "RN", pattern: "3w2, 2w7", requiredTraits: [TRAIT_WOUND_VAC] },
      { discipline: "PT", pattern: "2w8" },
    ],
    /** Wound care that decays: SN twice a week for three weeks, then every other week; PT evaluates first, frequencies follow. */
    woundCareSnDecay: [
      { discipline: "RN", pattern: "2w3, 1q2w6", requiredTraits: [TRAIT_WOUND_VAC] },
      { discipline: "PT", pattern: "eval" },
    ],
    chfSn: [{ discipline: "RN", pattern: "2w2, 1w7" }],
    orthoPtOt: [
      { discipline: "PT", pattern: "3w2, 2w6" },
      { discipline: "OT", pattern: "2w4" },
    ],
    strokeSnPtOtSlp: [
      { discipline: "RN", pattern: "1w9" },
      { discipline: "PT", pattern: "2w8" },
      { discipline: "OT", pattern: "2w6" },
      { discipline: "SLP", pattern: "1-2w8" },
      { discipline: "MSW", pattern: "1w2" },
    ],
    hhaSupport: [
      { discipline: "RN", pattern: "1w9" },
      { discipline: "HHA", pattern: "3w9" },
      { discipline: "MSW", pattern: "1w1" },
    ],
    copdSnHha: [
      { discipline: "RN", pattern: "2w3, 1w6" },
      { discipline: "HHA", pattern: "2w9" },
    ],
    therapyPtSlp: [
      { discipline: "PT", pattern: "2w6" },
      { discipline: "SLP", pattern: "1-2w6" },
    ],
  },
  scoreWeightsDisplay: { driveTime: 80, continuity: 20 },
  maxDriveMinutes: 65,
  candidatePoolOrder: ["overflow", "perDiemDeclared", "perDiemUndeclared", "dayOff"],
  confirmationChannel: "SMS",
  // The branch's confirmation round (3.3): the texts go at one, and an unanswered
  // text is the clinician's call after five.
  confirmationSendTime: "13:00",
  confirmationCutoffTime: "17:00",
  inflow: {
    trailingWeeks: 6,
    referralsPerWeekByDiscipline: { RN: 9, PT: 6, OT: 2, SLP: 1, MSW: 1, HHA: 3 },
    // Points per active case per week (field names kept for the persisted v2 shape).
    weeklyHoursPerCaseByDiscipline: { RN: 2.0, PT: 2.0, OT: 1.5, SLP: 1.0, MSW: 1.5, HHA: 3.0 },
    friMonSkew: 1.3,
    dischargesPerWeek: 7,
    weeklyHoursPerDischargedCase: 2.0,
  },
  targetPointsByEmployment: TARGET_POINTS_BY_EMPLOYMENT,
  ceilingPointsByEmployment: CEILING_POINTS_BY_EMPLOYMENT,
  documentationBlock: DOCUMENTATION_BLOCK,
};
